import React, { useState } from 'react';
import authService from '../services/authService';
import dashboardService from '../services/dashboardService';
import userManagementService from '../services/userManagementService';

/**
 * Manager Authentication Flow Test
 * Tests the complete flow from login to accessing manager-specific features
 */
const ManagerFlowTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [managerUser, setManagerUser] = useState(null);

  const flowTests = [
    {
      name: 'Step 1: Manager Login',
      test: async () => {
        console.log('[ManagerFlowTest] Testing manager login');
        const result = await authService.login('manager@company.com', 'admin123');
        setManagerUser(result.user);
        
        return {
          success: true,
          hasToken: !!result.token,
          hasUser: !!result.user,
          userEmail: result.user?.email,
          userRole: result.user?.role,
          correctRole: result.user?.role === 'hr_manager',
          tokenLength: result.token?.length
        };
      }
    },
    {
      name: 'Step 2: Token Verification',
      test: async () => {
        const isAuthenticated = authService.isAuthenticated();
        const storedUser = authService.getStoredUser();
        const token = authService.getToken();
        
        return {
          isAuthenticated,
          hasStoredUser: !!storedUser,
          hasToken: !!token,
          storedUserRole: storedUser?.role,
          correctRoleStored: storedUser?.role === 'hr_manager'
        };
      }
    },
    {
      name: 'Step 3: Backend Token Validation',
      test: async () => {
        console.log('[ManagerFlowTest] Testing backend token validation');
        const currentUser = await authService.getCurrentUser();
        
        return {
          success: true,
          userId: currentUser?.id,
          userEmail: currentUser?.email,
          userRole: currentUser?.role,
          isManager: currentUser?.role === 'hr_manager',
          matchesLoginUser: currentUser?.email === managerUser?.email
        };
      }
    },
    {
      name: 'Step 4: Dashboard Data Access',
      test: async () => {
        console.log('[ManagerFlowTest] Testing dashboard data access');
        try {
          const overview = await dashboardService.getDashboardOverview();
          return {
            success: true,
            hasOverview: !!overview,
            dataType: typeof overview,
            hasStats: !!overview?.stats
          };
        } catch (error) {
          // Dashboard might not be fully implemented
          return {
            success: false,
            error: error.message,
            note: 'Dashboard service may not be fully implemented yet'
          };
        }
      }
    },
    {
      name: 'Step 5: Manager Permissions Check',
      test: async () => {
        console.log('[ManagerFlowTest] Testing manager permissions');
        try {
          const canManage = await userManagementService.canManageUsers();
          return {
            success: true,
            canManageUsers: canManage,
            hasPermissions: canManage === true
          };
        } catch (error) {
          return {
            success: false,
            error: error.message,
            note: 'User management permissions check failed'
          };
        }
      }
    },
    {
      name: 'Step 6: Manager Features Access',
      test: async () => {
        console.log('[ManagerFlowTest] Testing manager-specific features');
        try {
          const userStats = await userManagementService.getUserStats();
          return {
            success: true,
            hasUserStats: !!userStats,
            canAccessUserManagement: true
          };
        } catch (error) {
          return {
            success: false,
            error: error.message,
            note: 'Manager features may require backend implementation'
          };
        }
      }
    }
  ];

  const runFlowTest = async () => {
    setIsRunning(true);
    setTestResults([]);
    
    const results = [];
    
    for (const test of flowTests) {
      console.log(`[ManagerFlowTest] Running: ${test.name}`);
      const startTime = Date.now();
      
      try {
        const result = await test.test();
        const duration = Date.now() - startTime;
        
        results.push({
          name: test.name,
          success: true,
          result,
          duration,
          error: null
        });
      } catch (error) {
        const duration = Date.now() - startTime;
        
        results.push({
          name: test.name,
          success: false,
          result: null,
          duration,
          error: error.message
        });
      }
      
      setTestResults([...results]);
      
      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setIsRunning(false);
  };

  const clearAuth = () => {
    authService.logout();
    setManagerUser(null);
    setTestResults([]);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          HR Manager Authentication Flow Test
        </h2>
        <p className="text-gray-600">
          Tests the complete authentication flow for HR managers from login to accessing role-specific features.
        </p>
      </div>

      {/* Current Manager Status */}
      {managerUser && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Current Manager Session:</h3>
          <div className="text-sm text-green-700">
            <div><strong>Name:</strong> {managerUser.name}</div>
            <div><strong>Email:</strong> {managerUser.email}</div>
            <div><strong>Role:</strong> {managerUser.role}</div>
            <div><strong>Active:</strong> {managerUser.is_active ? 'Yes' : 'No'}</div>
          </div>
        </div>
      )}

      {/* Control Buttons */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={runFlowTest}
          disabled={isRunning}
          className={`px-6 py-3 rounded-lg font-medium ${
            isRunning 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {isRunning ? 'Running Flow Test...' : 'Run Manager Flow Test'}
        </button>
        
        <button
          onClick={clearAuth}
          disabled={isRunning}
          className="px-6 py-3 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white"
        >
          Clear Auth & Reset
        </button>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Flow Test Results:</h3>
          
          {testResults.map((test, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border ${
                test.success 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className={`font-medium ${
                  test.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {test.success ? '✅' : '❌'} {test.name}
                </h4>
                <span className="text-sm text-gray-500">
                  {test.duration}ms
                </span>
              </div>
              
              {test.success && test.result && (
                <div className="text-sm">
                  <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                    {JSON.stringify(test.result, null, 2)}
                  </pre>
                </div>
              )}
              
              {!test.success && (
                <div className="text-sm text-red-700">
                  <strong>Error:</strong> {test.error}
                </div>
              )}
            </div>
          ))}
          
          {/* Summary */}
          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-2">Test Summary:</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-600">✅ Passed: </span>
                {testResults.filter(r => r.success).length}
              </div>
              <div>
                <span className="text-red-600">❌ Failed: </span>
                {testResults.filter(r => !r.success).length}
              </div>
            </div>
            
            <div className="mt-4 text-sm text-gray-600">
              <h5 className="font-medium mb-2">Expected Flow:</h5>
              <ol className="list-decimal list-inside space-y-1">
                <li>Manager logs in with valid credentials</li>
                <li>Token is stored and authentication state is set</li>
                <li>Backend validates the token on protected routes</li>
                <li>Manager can access dashboard data</li>
                <li>Manager has user management permissions</li>
                <li>Manager can access role-specific features</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerFlowTest;